import { useEffect, useMemo, useState } from "react";
import { Alert, Card, IconButton, Snackbar } from "@mui/material";
import { Edit, Delete as DeleteIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import { loadClientsPageFromBackend, deleteClient } from "app/services/client.service";
import { loadUsersByRoleFromBackend } from "app/services/user.service";
import * as AppConstants from "AppConstants";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { clientPageActions } from "store/client-page-slice";
import { useServerPaginatedGrid } from "app/hooks/useServerPaginatedGrid";
import { PaginatedDataGrid } from "app/components/common/PaginatedDataGrid";
import AddClientDialog from "./AddClientDialog";
import { ConfirmDialog } from "../ConfirmDialog";

// Frontend grid field -> backend sortBy column (allowlisted server-side).
// inoptraCrmUserId is absent on purpose: the column shows a name resolved in the browser from
// user-auth, so the database cannot order by what is displayed. The column is marked unsortable.
// @since NEXT_VERSION
const SORT_FIELD_MAP = {
  id: "id",
  name: "name",
  code: "code",
  contactPersonEmail: "contactPersonEmail",
};

const AllClientsView = () => {
  const [open, setOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("error");
  const [bdmUsers, setBdmUsers] = useState([]);

  // The client rows carry the relationship manager as a user id; the names live in user-auth. One
  // fetch of the BDM list feeds every row, rather than enriching each page server-side.
  // A failed lookup leaves the column blank -- it is informational, and the grid must still list the
  // clients. @since NEXT_VERSION
  // BDMs, matching the pool the Add/Edit Client dialog offers. It looked up managers until
  // HRMS-116 made this field the BDM mapping, which left every saved assignment resolving to no
  // name and the column blank -- the value was stored, but nothing on this screen showed it.
  // @since NEXT_VERSION
  useEffect(() => {
    loadUsersByRoleFromBackend(AppConstants.ROLE_BDM)
      .then((res) => setBdmUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBdmUsers([]));
  }, []);

  const bdmNamesById = useMemo(
    () => new Map(bdmUsers.map((user) => [user.id, user.fullName])),
    [bdmUsers]
  );

  const {
    rows,
    totalElements,
    page,
    pageSize,
    sortBy,
    sortDirection,
    searchQuery,
    loading,
    fetchPage,
    handlePaginationModelChange,
    handleSortModelChange,
    handleSearchChange,
  } = useServerPaginatedGrid({
    selector: (state) => state.clientPage,
    actions: clientPageActions,
    fetchFn: loadClientsPageFromBackend,
  });

  const editEventHandler = (data) => {
    setEditObject(data);
    setOpen(true);
  };

  const confirmDeleteDialogHandler = (data) => {
    setConfirmationMessage(
      `Are you sure you want to delete this Client (Name: '${data.name}', Code: '${data.code}')?`
    );
    setDeleteId(data.id);
    setConfirmOpen(true);
  };

  const deleteHandler = async () => {
    await deleteClient(deleteId)
      .then(() => {
        fetchPage();
      })
      .catch((error) => {
        const parsed = parseApiError(error);
        if (parsed.isUnauthorized) {
          handleAxiosError(error);
          return;
        }
        setSnackMessage(parsed.message || "Failed to delete client. Please try again.");
        setSnackSeverity("error");
        setSnackOpen(true);
      });
  };

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      cellClassName: "actions",
      getActions: (obj) => [
        <IconButton aria-label="Edit" onClick={() => editEventHandler(obj.row)} color="inherit">
          <Edit />
        </IconButton>,
        <IconButton aria-label="Delete" onClick={() => confirmDeleteDialogHandler(obj.row)} color="inherit">
          <DeleteIcon />
        </IconButton>,
      ],
    },
    { field: "id", headerName: "Id", width: 120 },
    { field: "name", headerName: "Client / Account Name", width: 250 },
    { field: "code", headerName: "Code", width: 250 },
    { field: "contactPersonEmail", headerName: "Contact Email", width: 250 },
    {
      field: "inoptraCrmUserId",
      headerName: "InOpTra's BDM / Client Relationship Manager",
      width: 280,
      sortable: false,
      valueGetter: (value, row) => bdmNamesById.get(row.inoptraCrmUserId) ?? "",
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            All Clients / Accounts
          </MDTypography>
        </MDBox>
        <MDBox height={500} mb={0} sx={{ overflowX: "auto" }}>
          <PaginatedDataGrid
            rows={rows}
            columns={columns}
            totalElements={totalElements}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            sortDirection={sortDirection}
            searchQuery={searchQuery}
            isLoading={loading}
            sortFieldMap={SORT_FIELD_MAP}
            defaultSortBy="name"
            defaultSortDirection="ASC"
            onPaginationModelChange={handlePaginationModelChange}
            onSortModelChange={handleSortModelChange}
            onSearchChange={handleSearchChange}
            onAdd={() => {
              setEditObject(null);
              setOpen(true);
            }}
            addLabel="Add Client / Account"
            columnVisibilityModel={{ id: false }}
          />
        </MDBox>
      </Card>
      <AddClientDialog
        open={open}
        setOpen={setOpen}
        refreshActivitiesDataHandler={() => fetchPage()}
        editObject={editObject}
      />
      <ConfirmDialog
        title={"Delete This Client?"}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity} sx={{ width: "100%" }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AllClientsView;
